import { useState, useEffect } from "react";
import { useI18n } from "../lib/i18n.jsx";
import { subscribeMarketplaceTeam, subscribeReviews, getTeamVersions, addMarketplaceReview } from "../lib/api.js";
import { navigateTo } from "../App.jsx";

export default function TeamDetail({ slug, user, onBack }) {
  const { t, lang } = useI18n();
  const [team, setTeam] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [versions, setVersions] = useState([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const unsub1 = subscribeMarketplaceTeam(slug, setTeam);
    return () => { unsub1(); };
  }, [slug]);

  useEffect(() => {
    if (!team) return;
    const unsub2 = subscribeReviews(team.id, setReviews);
    getTeamVersions(team.id).then(setVersions);
    return () => { unsub2(); };
  }, [team]);

  if (!team) return <div className="loading-state">{t("loading")}</div>;

  const descKey = `description_${lang}`;
  const description = team[descKey] || team.description_en || "";

  async function handleSubmitReview() {
    if (!user) { navigateTo("/?login=1"); return; }
    setSubmitting(true);
    try {
      await addMarketplaceReview({ teamId: team.id, rating, comment });
      setShowReviewForm(false);
      setComment("");
      setRating(5);
    } catch (e) {
      console.error("Review error:", e);
    }
    setSubmitting(false);
  }

  return (
    <div className="team-detail">
      <button className="btn btn-ghost back-btn" onClick={onBack}>← {t("back")}</button>

      <div className="team-detail-header">
        <div className="team-detail-info">
          <h1>{team.name}</h1>
          <div className="team-detail-meta">
            <span className="meta-rating big">★ {team.avgRating?.toFixed(1) || "—"}</span>
            <span>{t("review_count", { n: team.reviewCount || 0 })}</span>
            <span>·</span>
            <span>{t("install_count", { n: team.installCount || 0 })}</span>
            <span>·</span>
            <span className={`price-badge ${team.price ? "paid" : "free"}`}>
              {team.price ? `${team.price}€` : t("free")}
            </span>
          </div>
          <div className="team-detail-tags">
            {team.tags?.map((tag) => <span key={tag} className="tag">{tag}</span>)}
          </div>
        </div>
        <div className="team-detail-actions">
          <button className="btn btn-primary btn-lg" onClick={() => navigateTo(`/install/${team.id}`)}>
            {t("install")}
          </button>
        </div>
      </div>

      {description && (
        <section className="team-section">
          <h2>{t("description")}</h2>
          <div className="team-description">{description}</div>
        </section>
      )}

      {team.screenshots?.length > 0 && (
        <section className="team-section">
          <h2>{t("screenshots")}</h2>
          <div className="screenshots">
            {team.screenshots.map((url, i) => (
              <img key={i} src={url} alt={`${team.name} screenshot ${i + 1}`} className="screenshot" />
            ))}
          </div>
        </section>
      )}

      {versions.length > 0 && (
        <section className="team-section">
          <h2>{t("version")}</h2>
          <select className="version-select">
            {versions.map((v) => (
              <option key={v.id} value={v.id}>{v.id}</option>
            ))}
          </select>
        </section>
      )}

      <section className="team-section reviews-section">
        <div className="reviews-header">
          <h2>{t("reviews")} ({team.reviewCount || 0})</h2>
          <button className="btn btn-secondary" onClick={() => setShowReviewForm(!showReviewForm)}>
            {t("write_review")}
          </button>
        </div>

        {showReviewForm && (
          <div className="review-form">
            <div className="star-picker">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} className={`star ${n <= rating ? "active" : ""}`}
                        onClick={() => setRating(n)}>★</button>
              ))}
            </div>
            <textarea
              placeholder={t("your_comment")}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              className="review-textarea"
            />
            <button className="btn btn-primary" onClick={handleSubmitReview} disabled={submitting || !comment.trim()}>
              {submitting ? t("loading") : t("submit_review")}
            </button>
          </div>
        )}

        <div className="reviews-list">
          {reviews.map((r) => (
            <div key={r.id} className="review-card">
              <div className="review-header">
                <span className="review-author">{r.userName || "Anonymous"}</span>
                <span className="review-stars">{'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}</span>
              </div>
              <p className="review-comment">{r.comment}</p>
            </div>
          ))}
          {reviews.length === 0 && <p className="no-reviews">No reviews yet</p>}
        </div>
      </section>
    </div>
  );
}
